import React from "react";
import { useDrag } from "react-dnd";
import { Box, Button, Chip, IconButton, Stack, TextField, Tooltip } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useIntl } from "react-intl";
import RevaText from "components/common/RevaText";
import ItemTypes from "components/common/ItemTypes";
import { PREVIEW_HOME_CARDS } from "./branding-cards-config";
import { getDisabledPreviewCards, isCardAvailable } from "./branding-card-order";
import { cardImageUrl } from "./branding-helpers";

const PaletteCardChip = ({ card, label, onAdd }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.BRANDING_CARD,
    item: { uploadField: card.uploadField },
  }));

  return (
    <Chip
      ref={drag}
      icon={<DragIndicatorIcon />}
      label={label}
      onClick={() => onAdd(card.uploadField)}
      sx={{
        cursor: "grab",
        opacity: isDragging ? 0.45 : 1,
        justifyContent: "flex-start",
        width: "100%",
        "& .MuiChip-label": { flex: 1 },
      }}
    />
  );
};

const BrandingCardPalette = ({
  form,
  selectedUploadField,
  onAdd,
  onPatch,
  onImageUpload,
  uploadingField,
  localPreviews,
}) => {
  const intl = useIntl();
  const disabledCards = getDisabledPreviewCards(form);
  const selected = selectedUploadField
    ? PREVIEW_HOME_CARDS.find((card) => card.uploadField === selectedUploadField)
    : null;
  const selectedEnabled = selected && Boolean(form[selected.enabledKey]);

  const cardLabel = (card) =>
    (card.textKey && form[card.textKey]) || intl.formatMessage({ id: card.defaultTextId });

  return (
    <Stack spacing={2}>
      <Box>
        <RevaText variant="h6" sx={{ mb: 1 }}>
          {intl.formatMessage({ id: "gestion.branding.cards.palette" })}
        </RevaText>
        <RevaText variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {intl.formatMessage({ id: "gestion.branding.cards.paletteHint" })}
        </RevaText>
        <Stack spacing={1}>
          {disabledCards.length === 0 ? (
            <RevaText variant="body2" color="text.secondary">
              {intl.formatMessage({ id: "gestion.branding.cards.paletteEmpty" })}
            </RevaText>
          ) : (
            disabledCards.map((card) => (
              <PaletteCardChip key={card.uploadField} card={card} label={cardLabel(card)} onAdd={onAdd} />
            ))
          )}
        </Stack>
      </Box>

      {selected && selectedEnabled && isCardAvailable(form, selected) && (
        <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          <RevaText variant="subtitle2" sx={{ mb: 1.5 }}>
            {intl.formatMessage({ id: "gestion.branding.cards.editSelected" })}
          </RevaText>
          <Stack spacing={1.5}>
            <RevaText variant="body2" fontWeight={600}>
              {cardLabel(selected)}
            </RevaText>
            {selected.textKey && (
              <TextField
                size="small"
                fullWidth
                label={intl.formatMessage({ id: "gestion.branding.cards.text" })}
                value={form[selected.textKey] || ""}
                onChange={(event) => onPatch({ [selected.textKey]: event.target.value })}
              />
            )}
            {selected.linkKey && (
              <TextField
                size="small"
                fullWidth
                label={intl.formatMessage({ id: "gestion.branding.cards.link" })}
                value={form[selected.linkKey] || ""}
                onChange={(event) => onPatch({ [selected.linkKey]: event.target.value })}
              />
            )}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip title={intl.formatMessage({ id: "gestion.branding.cards.upload" })}>
                <IconButton component="label" size="small" disabled={uploadingField === selected.uploadField}>
                  <CloudUploadIcon fontSize="small" />
                  <input
                    hidden
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) onImageUpload(selected.uploadField, file);
                      event.target.value = "";
                    }}
                  />
                </IconButton>
              </Tooltip>
              {cardImageUrl(form, selected, localPreviews) && (
                <Box
                  component="img"
                  src={cardImageUrl(form, selected, localPreviews)}
                  alt=""
                  sx={{ height: 36, maxWidth: 80, objectFit: "cover", borderRadius: 0.5 }}
                />
              )}
            </Stack>
            <Button size="small" color="error" onClick={() => onPatch({ [selected.enabledKey]: false })}>
              {intl.formatMessage({ id: "gestion.branding.cards.removeFromPreview" })}
            </Button>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

export default BrandingCardPalette;
